import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useNavigate } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Get user data from Clerk
  const email = user?.primaryEmailAddress?.emailAddress || '';
  const currentName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

  useEffect(() => {
    if (isLoaded && user) {
      setEditedName(currentName);
    }
  }, [isLoaded, user, currentName]);

  const handleSaveProfile = async () => {
    if (!editedName.trim()) {
      setError('Name cannot be empty');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Parse name into first and last name
      const nameParts = editedName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Update Clerk user
      await user.update({
        firstName,
        lastName,
      });

      setSuccess('Profile updated successfully');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    return editedName !== currentName;
  };

  const handleDeleteAccount = async () => {
    if (confirmEmail !== email) {
      setError('Email does not match');
      return;
    }

    try {
      setDeleting(true);
      setError('');

      // Delete user from Clerk - this triggers the webhook for cascade deletion
      await user.delete();

      // Sign out and redirect
      signOut(() => navigate('/sign-in'));
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.message || 'Failed to delete account');
      setDeleting(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
    setConfirmEmail('');
    setError('');
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setConfirmEmail('');
    setError('');
  };

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 500 }}>
        Settings
      </Typography>

      {/* Profile Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Profile Information
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Email"
              value={email}
              disabled
              variant="outlined"
              helperText="Email cannot be changed"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Name"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              variant="outlined"
            />
          </Box>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveProfile}
            disabled={saving || !hasChanges()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      {/* Danger Zone Card */}
      <Card sx={{ borderColor: 'error.main', border: 1 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1, color: 'error.main' }}>
            Danger Zone
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Once you delete your account, there is no going back. This will permanently delete all your data including:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3, color: 'text.secondary' }}>
            <li>All vocabulary words and content</li>
            <li>All practice sessions and progress</li>
            <li>Your profile information</li>
          </Box>

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={handleOpenDeleteDialog}
          >
            Delete Account
          </Button>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Delete Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            This action cannot be undone. This will permanently delete your account and all associated data.
          </DialogContentText>

          <DialogContentText sx={{ mb: 2, fontWeight: 500 }}>
            Please type <strong>{email}</strong> to confirm:
          </DialogContentText>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Confirm Email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            variant="outlined"
            autoFocus
          />

          <DialogContentText sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
            A confirmation email will be sent to your email address after deletion.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={deleting || confirmEmail !== email}
          >
            {deleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
